import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Box,
  Alert,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { showNotification } from "../../../utils/notifications";
import AppService from "../../../services/AppService";
import { getAuthToken } from "../../../utils/authUtils";
import OrganizationConfigService from "../../../services/OrganizationConfigService";

/**
 * Self-Assignment Settings component
 * A simplified component focused only on saving self-assignment settings
 */
const SelfAssignmentSetting = ({ organizationId }) => {
  const [formData, setFormData] = useState({
    allowSelfAssign: false,
    allowTicketSelfAssign: false,
    selfAssignLimit: 5,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // Determine role from JWT to enforce ROOT_ADMIN-only edits
  const [isRootAdmin, setIsRootAdmin] = useState(false);

  useEffect(() => {
    try {
      const authInfo = localStorage.getItem('authInfo');
      const token = authInfo ? JSON.parse(authInfo)?.token : null;
      if (token) {
        const [, payloadB64] = token.split('.') || [];
        if (payloadB64) {
          const payload = JSON.parse(atob(payloadB64));
          const roles = (payload?.roles || []).map(r => r.authority || r).concat(payload?.authorities || []);
          setIsRootAdmin(roles.includes('ROLE_ROOT_ADMIN'));
        }
      }
    } catch (e) {
      setIsRootAdmin(false);
    }
  }, []);

  // Load existing configuration on component mount
  useEffect(() => {
    setLoading(true);
    
    // Try to get existing config
    AppService.get(`/api/organization-config/organization/${organizationId}`)
      .then(response => {
        console.log("Loaded existing config:", response);
        // Handle response variations (data might be in response or response.data)
        const config = response.data || response;
        
        setFormData({
          allowSelfAssign: (config.allowSelfAssign === true) || (config.allow_workflow_self_assign === true),
          allowTicketSelfAssign: config.allowTicketSelfAssign === true,
          selfAssignLimit: config.selfAssignLimit || 5
        });
        setLoading(false);
      })
      .catch(error => {
        console.log("Error loading config or none exists yet:", error);
        setLoading(false);
      });
  }, [organizationId]);

  // Handle form input changes
  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    const newValue = type === "checkbox" ? checked : value;
    
    setFormData({
      ...formData,
      [name]: newValue
    });
  };

  // Handle save (no nested form submit to avoid conflicts with parent form)
  const handleSave = () => {
    setSaving(true);
    setError("");
    setSuccess("");
    
    // Prepare data with only the essential self-assignment fields
    // Using real data as required
    const dataToSend = {
      organizationId,
      // Real data keys; include alias to match backend mapping
      allowSelfAssign: formData.allowSelfAssign === true,
      allow_workflow_self_assign: formData.allowSelfAssign === true,
      allowTicketSelfAssign: formData.allowTicketSelfAssign === true,
      allow_ticket_self_assign: formData.allowTicketSelfAssign === true,
      selfAssignLimit: parseInt(formData.selfAssignLimit) || 0,
      self_assign_limit: parseInt(formData.selfAssignLimit) || 0
    };
    
    console.log("Saving self-assignment settings with real data:", dataToSend);
    
    // Verify authentication is available
    const token = getAuthToken();
    
    if (!token) {
      console.error("No authentication token available - authentication required");
      setError("Authentication required. Please log in and try again.");
      setSaving(false);
      showNotification("Authentication required", "error");
      return; // Stop execution if no token is available
    }
    
    console.log("Authentication token is available, proceeding with request");
    
    // Use upsert-by-organizationId to avoid nested forms issues and simplify saving
    OrganizationConfigService.updateByOrganizationId(organizationId, dataToSend)
      .then(() => {
        setSuccess("Self-assignment settings saved successfully");
        setSaving(false);
        showNotification("Self-assignment settings saved", "success");
      })
      .catch(error => {
        console.error("Error saving settings:", error);
        const errorMessage = error?.response?.data?.message || error.message || "Unknown error";
        setError(`Failed to save settings: ${errorMessage}`);
        setSaving(false);
        showNotification("Error saving settings", "error");
      });
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          Self-Assignment Settings
        </Typography>
        
        {loading ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        ) : (
          <div>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowSelfAssign}
                      onChange={handleChange}
                      name="allowSelfAssign"
                      color="primary"
                      disabled={!isRootAdmin}
                    />
                  }
                  label="Allow Workflow Self-Assignment"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowTicketSelfAssign}
                      onChange={handleChange}
                      name="allowTicketSelfAssign"
                      color="primary"
                      disabled={!isRootAdmin}
                    />
                  }
                  label="Allow Ticket Self-Assignment"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Self-Assignment Limit"
                  type="number"
                  name="selfAssignLimit"
                  value={formData.selfAssignLimit}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{ inputProps: { min: 0 } }}
                  helperText="Maximum number of tickets a user can self-assign"
                  disabled={!isRootAdmin}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  disabled={saving || !organizationId || !isRootAdmin}
                >
                  {saving ? "Saving..." : "Save Settings"}
                </Button>
              </Grid>
            </Grid>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SelfAssignmentSetting;
